/**
 * Clients route.
 *
 * Lists `users/{uid}/clients` (via `useClients`) — the "quem é o cliente"
 * profiles that feed the agent's briefings. Create/edit happen in a dialog and
 * write straight to Firestore (mirroring the agent's `upsertClientProfile`).
 * `matchKeywords` is edited as a comma-separated string and stored as an array.
 *
 * Kept mounted at path '/clients'. Exported as `ClientsRoute`.
 */
import type { ClientProfileDoc } from '@agente/shared'
import { Loader, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useClients } from '@/hooks/useClients'
import { useAuth } from '@/lib/auth'
import { type ClientProfileInput, createClient, deleteClient, updateClient } from '@/lib/firestore'

/** Split a comma-separated keyword string into a trimmed, de-duplicated array. */
function parseKeywords(raw: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of raw.split(',')) {
    const kw = part.trim()
    if (kw && !seen.has(kw.toLowerCase())) {
      seen.add(kw.toLowerCase())
      out.push(kw)
    }
  }
  return out
}

export function ClientsRoute() {
  const { clients, loading, error } = useClients()
  const [editing, setEditing] = useState<ClientProfileDoc | null>(null)
  const [deleting, setDeleting] = useState<ClientProfileDoc | null>(null)
  const [creating, setCreating] = useState(false)

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 py-6 md:p-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Clientes</h1>
          <p className="text-muted-foreground text-sm">
            Perfis usados pelo agente para reconhecer o cliente em reuniões e briefings.
          </p>
        </div>
        <Button type="button" className="shrink-0 shadow-sm" onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          Novo cliente
        </Button>
      </header>

      {error ? (
        <p className="text-destructive text-sm">Não foi possível carregar os clientes: {error}</p>
      ) : loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : clients.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="flex flex-col gap-2">
          {clients.map((c) => (
            <ClientRow
              key={c.id}
              client={c}
              onEdit={() => setEditing(c)}
              onDelete={() => setDeleting(c)}
            />
          ))}
        </ul>
      )}

      {/* Create dialog */}
      <ClientDialog
        key={creating ? 'create' : 'create-closed'}
        open={creating}
        onOpenChange={setCreating}
        client={null}
      />
      {/* Edit dialog (keyed by id so fields reset between clients) */}
      <ClientDialog
        key={editing?.id ?? 'edit-closed'}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
        client={editing}
      />
      {/* Delete confirmation */}
      <DeleteClientDialog
        client={deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
      />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="border-border flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center">
      <Users className="text-muted-foreground size-8" aria-hidden="true" />
      <p className="text-muted-foreground text-sm">
        Nenhum cliente cadastrado. Adicione perfis para enriquecer os briefings do agente.
      </p>
    </div>
  )
}

function ClientRow({
  client,
  onEdit,
  onDelete,
}: {
  client: ClientProfileDoc
  onEdit: () => void
  onDelete: () => void
}) {
  const subtitle = [client.company, client.sector].filter(Boolean).join(' · ')
  return (
    <li className="border-border bg-card flex items-start gap-3 rounded-lg border p-3 shadow-sm">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{client.name}</p>
        {subtitle ? <p className="text-muted-foreground text-sm">{subtitle}</p> : null}
        {client.notes ? (
          <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">{client.notes}</p>
        ) : null}
        {client.matchKeywords.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {client.matchKeywords.map((kw) => (
              <span
                key={kw}
                className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs"
              >
                {kw}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Editar ${client.name}`}
          onClick={onEdit}
        >
          <Pencil className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Excluir ${client.name}`}
          className="text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </li>
  )
}

function DeleteClientDialog({
  client,
  onOpenChange,
}: {
  client: ClientProfileDoc | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [deleting, setDeleting] = useState(false)

  async function handleDelete() {
    if (!client || !uid) return
    setDeleting(true)
    try {
      await deleteClient(uid, client.id)
      toast.success('Cliente excluído')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível excluir o cliente', { description: message })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={client !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir cliente</DialogTitle>
          <DialogDescription>
            {client
              ? `Tem certeza de que deseja excluir "${client.name}"? Esta ação é permanente e o agente deixará de reconhecer este cliente.`
              : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Loader className="size-4 animate-spin" /> : null}
            Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ClientDialog({
  open,
  onOpenChange,
  client,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  client: ClientProfileDoc | null
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [name, setName] = useState(client?.name ?? '')
  const [company, setCompany] = useState(client?.company ?? '')
  const [sector, setSector] = useState(client?.sector ?? '')
  const [notes, setNotes] = useState(client?.notes ?? '')
  const [keywords, setKeywords] = useState(client?.matchKeywords.join(', ') ?? '')
  const [saving, setSaving] = useState(false)

  const canSave = name.trim().length > 0 && !saving

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSave || !uid) return
    const input: ClientProfileInput = {
      name: name.trim(),
      company: company.trim() || undefined,
      sector: sector.trim() || undefined,
      notes: notes.trim() || undefined,
      matchKeywords: parseKeywords(keywords),
    }
    setSaving(true)
    try {
      if (client) {
        await updateClient(uid, client.id, input)
        toast.success('Cliente atualizado')
      } else {
        await createClient(uid, input)
        toast.success('Cliente criado')
      }
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível salvar o cliente', { description: message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{client ? 'Editar cliente' : 'Novo cliente'}</DialogTitle>
            <DialogDescription>
              Esses dados ajudam o agente a identificar o cliente na sua agenda.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="client-name">Nome</Label>
            <Input
              id="client-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do cliente ou contato"
              required
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="client-company">Empresa (opcional)</Label>
            <Input
              id="client-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Razão social ou grupo"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="client-sector">Setor (opcional)</Label>
            <Input
              id="client-sector"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              placeholder="Ex.: energia, varejo, financeiro"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="client-notes">Observações (opcional)</Label>
            <Textarea
              id="client-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Contexto relevante, histórico, pontos de atenção."
              rows={3}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="client-keywords">Palavras-chave</Label>
            <Input
              id="client-keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="acme, contrato acme, joão silva"
            />
            <p className="text-muted-foreground text-xs">
              Separadas por vírgula. Usadas para casar com assuntos e participantes na agenda.
            </p>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSave}>
              {saving ? <Loader className="size-4 animate-spin" /> : null}
              {client ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ClientsRoute
